/**
 * TextField — the shared labelled input from the mock's field anatomy (`.field` / `.field__label`
 * / `.field__input`), sized for the referee flow's older-user target rule (≥44px tap height,
 * ux-flows §4). Presentational + accessible: a real `<label htmlFor>`, an error wired via
 * `aria-describedby` + `aria-invalid`, and RHF's ref forwarded so it registers cleanly.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Optional leading glyph (16px), echoing the mock's field icons. */
  icon?: ReactNode;
  /** Sub-label helper text under the input. */
  hint?: string;
  /** Validation message; when set the field renders its invalid state. */
  error?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, icon, hint, error, id, className, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mt-3">
      <label htmlFor={inputId} className="mb-1.5 block text-[12px] font-medium text-ink-quiet">
        {label}
      </label>
      <div
        className={cn(
          'flex items-center gap-2.5 rounded-md border bg-surface px-3.5 transition-colors duration-ui ease-thread',
          'focus-within:border-sage focus-within:ring-2 focus-within:ring-sage-deep/20',
          error ? 'border-coral-deep' : 'border-line-2',
        )}
      >
        {icon && <span className="flex-none text-ink-38">{icon}</span>}
        <input
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'min-h-[44px] w-full bg-transparent py-2 font-body text-[14px] font-medium text-ink outline-none',
            'placeholder:font-normal placeholder:text-ink-quiet',
            className,
          )}
          {...props}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[11.5px] leading-snug text-ink-quiet">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-[11.5px] font-medium leading-snug text-coral-text">
          {error}
        </p>
      )}
    </div>
  );
});
